import { request } from './api';

export interface MailAccount {
  id: string;
  label: string;
  fromName: string;
  fromEmail: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  imapHost: string;
  imapPort: number;
  username: string;
  dailyLimit: number;
  warmupStartedAt: string;
  signature: string | null;
  footerAddress: string | null;
  active: boolean;
  pausedReason: string | null;
  lastError: string | null;
  lastCheckedAt: string | null;
  sentFolder: string | null;
  sentToday: number;
  todayLimit: number;
  sent7d: number;
  bounced7d: number;
}

export type MailAccountInput = Partial<
  Pick<
    MailAccount,
    | 'label' | 'fromName' | 'fromEmail' | 'smtpHost' | 'smtpPort' | 'smtpSecure' | 'imapHost' | 'imapPort'
    | 'username' | 'dailyLimit' | 'signature' | 'footerAddress' | 'active'
  >
> & { password?: string; resume?: boolean; restartWarmup?: boolean };

export interface StepInput {
  delayDays: number;
  subject: string;
  body: string;
}
export interface Step extends StepInput {
  id: string;
  position: number;
}

export interface Preset {
  key: string;
  label: string;
  description: string;
  steps: StepInput[];
}

export interface CampaignStats {
  enrolled: number;
  active: number;
  completed: number;
  replied: number;
  bounced: number;
  unsubscribed: number;
  stopped: number;
  emailsSent: number;
  leadsContacted: number;
  leadsOpened: number;
  leadsClicked: number;
  opens: number;
  openRate: number;
  replyRate: number;
}

export type CampaignStatus = 'draft' | 'active' | 'paused' | 'completed';

export interface Campaign {
  id: string;
  name: string;
  service: string;
  status: CampaignStatus;
  mailAccountId: string;
  sendFromHour: number;
  sendToHour: number;
  trackOpens: boolean;
  trackClicks: boolean;
  skipStrictCountries: boolean;
  pausedReason: string | null;
  createdAt: string;
  stepCount?: number;
  stats?: CampaignStats;
  mailAccount?: { id?: string; fromEmail: string; fromName?: string };
}

export interface Enrollment {
  id: string;
  status: 'active' | 'completed' | 'replied' | 'bounced' | 'unsubscribed' | 'stopped';
  nextStep: number;
  nextSendAt: string | null;
  lastSentAt: string | null;
  stoppedReason: string | null;
  lead: { id: string; businessName: string; email: string | null; city: string | null; country: string | null; opportunityScore: number };
  outreach: { stepPosition: number | null; sentAt: string; status: string; openCount: number; openedAt: string | null; clickCount: number; repliedAt: string | null }[];
}

export interface CampaignDetail extends Campaign {
  steps: Step[];
  stats: CampaignStats;
  enrollments: Enrollment[];
}

export interface EnrollFilter {
  minScore?: number;
  city?: string;
  country?: string;
  category?: string;
  hasWebsite?: boolean;
  onlyNew?: boolean;
  limit?: number;
}

export interface InboundEmail {
  id: string;
  kind: 'reply' | 'auto_reply' | 'bounce' | 'unsubscribe';
  fromEmail: string;
  fromName: string | null;
  subject: string | null;
  snippet: string;
  receivedAt: string;
  handled: boolean;
  lead: { id: string; businessName: string; status: string } | null;
  campaignLead: { campaign: { id: string; name: string } } | null;
}

const json = (body: unknown) => ({ body: JSON.stringify(body) });

export const mailApi = {
  accounts: () => request<MailAccount[]>('/mail-accounts'),
  createAccount: (body: MailAccountInput) => request<MailAccount>('/mail-accounts', { method: 'POST', ...json(body) }),
  updateAccount: (id: string, body: MailAccountInput) =>
    request<MailAccount>(`/mail-accounts/${id}`, { method: 'PATCH', ...json(body) }),
  testAccount: (id: string, to?: string) =>
    request<{ ok: true; sentTo: string }>(`/mail-accounts/${id}/test`, { method: 'POST', ...json(to ? { to } : {}) }),
  deleteAccount: (id: string) => request<void>(`/mail-accounts/${id}`, { method: 'DELETE' }),

  presets: () => request<{ presets: Preset[]; variables: string[] }>('/campaigns/presets'),
  campaigns: () => request<Campaign[]>('/campaigns'),
  campaign: (id: string, status?: string) =>
    request<CampaignDetail>(`/campaigns/${id}${status ? `?status=${status}` : ''}`),
  createCampaign: (body: { name: string; service: string; mailAccountId: string }) =>
    request<Campaign>('/campaigns', { method: 'POST', ...json(body) }),
  updateCampaign: (id: string, body: Partial<Campaign>) =>
    request<Campaign>(`/campaigns/${id}`, { method: 'PATCH', ...json(body) }),
  saveSteps: (id: string, steps: StepInput[]) => request<Step[]>(`/campaigns/${id}/steps`, { method: 'PUT', ...json(steps) }),
  setStatus: (id: string, status: 'active' | 'paused' | 'completed') =>
    request<Campaign>(`/campaigns/${id}/status`, { method: 'POST', ...json({ status }) }),
  enroll: (id: string, body: { leadIds?: string[]; filter?: EnrollFilter }) =>
    request<{ added: number; skipped: Record<string, number> }>(`/campaigns/${id}/leads`, { method: 'POST', ...json(body) }),
  stopEnrollment: (id: string, enrollmentId: string) =>
    request<void>(`/campaigns/${id}/leads/${enrollmentId}`, { method: 'DELETE' }),
  preview: (id: string, step: number, leadId?: string) =>
    request<{ lead: { businessName: string; email: string | null }; subject: string; body: string }>(
      `/campaigns/${id}/preview?step=${step}${leadId ? `&leadId=${leadId}` : ''}`,
    ),
  deleteCampaign: (id: string) => request<void>(`/campaigns/${id}`, { method: 'DELETE' }),

  inbox: (params: { kind?: string; handled?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.kind) q.set('kind', params.kind);
    if (params.handled !== undefined) q.set('handled', String(params.handled));
    return request<{ unhandledReplies: number; items: InboundEmail[] }>(`/inbox?${q.toString()}`);
  },
  setHandled: (id: string, handled: boolean) => request<InboundEmail>(`/inbox/${id}`, { method: 'PATCH', ...json({ handled }) }),

  suppressions: () => request<{ email: string; reason: string; createdAt: string }[]>('/suppressions'),
  addSuppression: (email: string) => request<{ email: string }>('/suppressions', { method: 'POST', ...json({ email }) }),
  removeSuppression: (email: string) => request<void>(`/suppressions/${encodeURIComponent(email)}`, { method: 'DELETE' }),
};
